'use client';

/**
 * Page View Tracker Component
 *
 * IMPORTANT: This component should ONLY be used on the home page.
 * It explicitly tracks PageView events with Facebook Pixel.
 */
import { useEffect } from 'react';
import { usePathname, useSearchParams } from 'next/navigation';
import { useTrackEvent } from '../hooks/useTrackEvent';
import { getConsentData } from '@/components/cookie-modal';
import * as gtag from '../gtag';
import * as fpixel from '../fpixel';

/**
 * Component for explicitly tracking page views.
 * Proper page tracking implementation that respects user consent.
 */
export function PageViewTracker() {
  const pathname = usePathname();
  const searchParams = useSearchParams();
  const {
    trackPageView
  } = useTrackEvent();
  useEffect(() => {
    // Only track on the client
    if (typeof window === 'undefined') {
      return;
    }

    // Get the full path including search params
    const search = searchParams?.toString() ? `?${searchParams.toString()}` : '';
    const fullpath = pathname + search;

    // Get the absolute URL to use as event_source_url
    const absoluteUrl = `${window.location.origin}${fullpath}`;

    // Check for cookie consent
    const consentData = getConsentData();

    // Handle Google Analytics tracking (requires measurement consent)
    if (consentData?.measurement) {
      // For all pages, track in Google Analytics if available
      if (typeof window.gtag === 'function') {
        gtag.pageview(fullpath);
      }
    }

    // Handle Meta Pixel tracking (requires marketing consent)
    if (consentData?.marketing) {
      // Track Meta Pixel PageView with explicit event_source_url
      if (typeof window.fbq === 'function') {
        // Use the enhanced pageview function that includes event_source_url
        fpixel.pageview(absoluteUrl);
      }
    }
  }, [pathname, searchParams, trackPageView]);

  // Component doesn't render anything
  return null;
}